/* global __APP_VERSION__ */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'
import CheckBold from '@assets/check-bold.svg?react'
import { Rating } from '@mui/material'
import { Assets, Button, Select, SelectItem, Star, StarFilled, TextField } from '@nexds/web'
import { useTheme } from 'styled-components'

import { ReviewProps, SendReviewUseCase } from '@/domain/useCases/General/SendReviewUseCase'

import { ReviewSentProps } from '../../types/FeedbackCollector'
import {
  ActionButton,
  Body,
  Header,
  InputContainer,
  Message,
  RatingContainer,
  RatingLabel,
  ReviewButton,
  ReviewPopupContainer,
  Title,
  TypeButton,
  TypeLabel,
  TypeSelector,
  TypeWrapper
} from './FeedbackCollectorFeature.styles'

import { useI18n } from '@/presentation/hooks/useI18n'
import { useScreenDimensions } from '@/presentation/hooks/useScreenDimensions'
import { BackdropImperativeInterface } from '@/presentation/interfaces/BackdropImperativeInterface'
import { FeedbackFeatureImperativeInterface } from '@/presentation/interfaces/FeedbackFeatureImperativeInterface'
import { appZIndex } from '@/presentation/utils/zIndexMapper'

interface FeedbackCollectorFeatureProps {
  passiveFeedbackEnabled?: boolean
  onDisablePassiveFeedback?: () => void
  forceCompactButton?: boolean
}

// TODO: I18n
function FeedbackCollectorFeature(props: FeedbackCollectorFeatureProps) {
  const theme = useTheme()
  const { t } = useI18n()
  const VERSION = __APP_VERSION__
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm()

  const { screenWidth } = useScreenDimensions()

  const [isAppInFullscreen, setIsAppInFullscreen] = useState(false)
  const [ratingValue, setRatingValue] = useState(0)
  const [modalType, setModalType] = useState<'active' | 'passive'>('active')

  const [isOpen, setIsOpen] = useState(false)
  const [reviewSent, setReviewSent] = useState<ReviewSentProps>({
    wasSent: false,
    isSuccess: false
  })

  const headerMessage = useMemo(() => {
    if (reviewSent.wasSent) {
      if (reviewSent.isSuccess)
        return (
          <>
            <CheckBold />
            {t('FEEDBACK_COLLECTOR_HEADER_MESSAGE_FEEDBACK-SENT')}
          </>
        )
      else return `⚠️ ${t('FEEDBACK_COLLECTOR_HEADER_MESSAGE_FEEDBACK-ERROR')}`
    } else if (modalType === 'active') {
      return 'Queremos ouvir seu feedback'
    } else {
      return 'Como foi sua experiência?'
    }
  }, [modalType, reviewSent])

  const isCompactButton = isAppInFullscreen || props.forceCompactButton

  const reviewButtonLabel = useMemo(() => {
    if (isCompactButton) {
      if (isOpen) {
        return <Assets.Close2 size={16} color={theme.colors.secondaryD1} />
      } else {
        return '📢'
      }
    } else {
      if (isOpen) {
        if (screenWidth <= 620) {
          return <Assets.Close2 size={16} color={theme.colors.neutralL5} />
        } else {
          return t('FEEDBACK_COLLECTOR_MAIN-BUTTON_LABEL_CLOSE')
        }
      } else if (screenWidth <= 620) {
        return '📢'
      } else {
        return `📢 ${t('FEEDBACK_COLLECTOR_MAIN-BUTTON_LABEL_SEND')}`
      }
    }
  }, [screenWidth, isOpen, isCompactButton, t, theme.colors.neutralL5, theme.colors.secondaryD1])

  const sendReview = async (reviewBody: ReviewProps) => {
    const result = await new SendReviewUseCase().execute(reviewBody)
    if (result.isFailure) {
      console.error(result.error)
      return false
    }
    return true
  }

  useEffect(() => {
    setReviewSent({ isSuccess: false, wasSent: false })
  }, [modalType])

  useEffect(() => {
    const unsubscribe = FeedbackFeatureImperativeInterface.subscribe(({ isAppInFullscreen, type }) => {
      if (isAppInFullscreen !== undefined) setIsAppInFullscreen(isAppInFullscreen)
      if (type) {
        if (type === 'passive') {
          if (props.passiveFeedbackEnabled) {
            setModalType(type)
            setIsOpen(true)
          }
        } else {
          setModalType(type)
          setIsOpen(true)
        }
      }
    })

    return () => {
      unsubscribe() // descadastra só esta inscrição, sem fechar o Subject
    }
  }, [props.passiveFeedbackEnabled])

  const onSubmit = async (data: FieldValues) => {
    const body: ReviewProps = {
      rating: ratingValue,
      type: 'suggestion',
      feature: 'general',
      comment: data['comment'],
      version: VERSION,
      product: 'planning-app'
    }
    const isSuccess = await sendReview(body)

    if (isSuccess) {
      reset()
      setRatingValue(0)
      if (modalType === 'passive' && props.onDisablePassiveFeedback) {
        props.onDisablePassiveFeedback()
      }
    }
    setReviewSent({ wasSent: true, isSuccess })
  }

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setReviewSent({ wasSent: false, isSuccess: false })
    if (modalType === 'passive' && props.onDisablePassiveFeedback) {
      props.onDisablePassiveFeedback()
    }
  }, [modalType, props.onDisablePassiveFeedback])

  const handleOpen = useCallback(() => {
    setModalType('active')
    setIsOpen((prev) => !prev)
  }, [setIsOpen])

  useEffect(() => {
    if (isOpen) {
      BackdropImperativeInterface.show(appZIndex.FEEDBACK_COLLECTOR_FEATURE, 0.7, () => setIsOpen(false))
    } else {
      BackdropImperativeInterface.hide()
    }
  }, [isOpen])

  return (
    <>
      <ReviewButton isAppInFullscreen={isCompactButton} onClick={handleOpen}>
        {reviewButtonLabel}
      </ReviewButton>
      {isOpen && (
        <ReviewPopupContainer>
          <Header>
            <Title>{headerMessage}</Title>
            <ActionButton onClick={handleClose}>
              <Assets.Close size={32} color={theme.colors.secondaryL3} />
            </ActionButton>
          </Header>
          <Body flexDirection={'column'} gap={16}>
            {reviewSent.wasSent ? (
              <>
                <Message>
                  {reviewSent.isSuccess ? (
                    <>
                      Obrigado!
                      <br />
                      <br />
                      Sua opinião faz toda a diferença para que possamos continuar melhorando.
                    </>
                  ) : (
                    <>Parece que um erro inesperado aconteceu. Tente novamente mais tarde.</>
                  )}
                </Message>
                <Button
                  label={reviewSent.isSuccess ? 'Fechar' : 'Voltar'}
                  color="secondary"
                  variant="filled"
                  size="sm"
                  onPress={
                    reviewSent.isSuccess ? handleClose : () => setReviewSent((prev) => ({ ...prev, wasSent: false }))
                  }
                />
                {/* {reviewSent.isSuccess && (
                  <Button
                    label="Enviar outro feedback"
                    color="secondary"
                    variant="outline"
                    size="sm"
                    onPress={() => {
                      setReviewSent({ wasSent: false, isSuccess: false })
                    }}
                  />
                )} */}
              </>
            ) : modalType === 'active' ? (
              <>
                <RatingContainer>
                  <RatingLabel>Avaliação:</RatingLabel>
                  <Rating
                    icon={<StarFilled color={theme.colors.yellowL0} size={20} />}
                    emptyIcon={<StarFilled color={theme.colors.secondaryL1} size={20} />}
                    name="rating-stars"
                    value={ratingValue}
                    onChange={(_, newValue) => {
                      setRatingValue(newValue)
                    }}
                  />
                </RatingContainer>
                <InputContainer>
                  <TextField
                    autoFocus
                    multiline
                    maxLength={1000}
                    helpMessage={errors?.comment ? 'Escreva um feedback' : ''}
                    error={!!errors?.comment}
                    placeholder="Escreva aqui sua sugestão, crítica ou elogio. Seu feedback é essencial pra essa fase beta."
                    {...register('comment', { required: true })}
                  />
                </InputContainer>
                <Button
                  label="Enviar"
                  color="secondary"
                  variant="filled"
                  size={'sm'}
                  onPress={handleSubmit(onSubmit)}
                />
              </>
            ) : modalType === 'passive' ? (
              <>
                <RatingContainer>
                  <RatingLabel>Avaliação:</RatingLabel>
                  <Rating
                    icon={<StarFilled color={theme.colors.yellowL0} size={20} />}
                    emptyIcon={<StarFilled color={theme.colors.secondaryL1} size={20} />}
                    name="rating-stars"
                    value={ratingValue}
                    onChange={(_, newValue) => {
                      setRatingValue(newValue)
                    }}
                  />
                </RatingContainer>
                <InputContainer>
                  <TextField
                    autoFocus
                    multiline
                    helpGutter={true}
                    labelGutter={false}
                    maxLength={1000}
                    helpMessage={errors?.comment ? 'Escreva um feedback' : ''}
                    error={!!errors?.comment}
                    placeholder="Conte pra gente como foi usar essa funcionalidade"
                    {...register('comment', { required: true })}
                  />
                </InputContainer>
                <Button
                  label="Enviar"
                  color="secondary"
                  variant="filled"
                  size={'sm'}
                  onPress={handleSubmit(onSubmit)}
                />
              </>
            ) : null}
          </Body>
        </ReviewPopupContainer>
      )}
    </>
  )
}

export { FeedbackCollectorFeature }
